<template>
  <nav
    class="flex flex-wrap gap-5 justify-between px-7 py-3 w-full max-md:px-5"
  >
    <!-- Left section: App Switcher + Logo + Tabs -->
    <div
      class="flex flex-wrap gap-10 text-sm font-medium leading-none text-gray-600"
    >
      <!-- App Switcher -->
      <div class="flex items-center gap-4">
        <AppSwitcherMenu />
        <div class="mt-0.5">
          <div v-html="INSIGHTS_LOGO_ICON"></div>
        </div>
        <span class="text-lg font-semibold text-gray-800">Expert Insights</span>
      </div>

      <!-- Navigation Tabs -->
      <div class="flex flex-wrap gap-5 overflow-x-auto whitespace-nowrap">
        <!-- Search Tab -->
        <div
          class="flex overflow-hidden items-center py-2 pr-3 pl-2 rounded-md"
        >
          <div class="flex gap-3 items-center self-stretch my-auto">
            <BaseButton
              type="button"
              class="flex gap-2"
              @click="navigateToSearch"
            >
              <div
                v-html="INSIGHTS_SEARCH_ICON"
                :class="{
                  'text-primary-purple': activeTab === 'search',
                  'text-gray-500': activeTab !== 'search',
                }"
                class="w-5 h-5"
              ></div>
              <span
                class="self-stretch my-auto"
                :class="{
                  'text-primary-purple': activeTab === 'search',
                  'text-gray-600': activeTab !== 'search',
                }"
              >
                Search
              </span>
            </BaseButton>
          </div>
        </div>

        <!-- Watchlist Tab -->
        <div
          class="flex overflow-hidden items-center py-2 pr-3 pl-2 whitespace-nowrap rounded-md"
        >
          <div class="flex gap-3 items-center self-stretch my-auto">
            <BaseButton
              type="button"
              class="flex gap-2"
              @click="navigateToWatchlist"
            >
              <div
                v-html="WATCHLIST_ICON"
                :class="{
                  'text-primary-purple': activeTab === 'watchlist',
                  'text-gray-500': activeTab !== 'watchlist',
                }"
                class="w-5 h-5"
              ></div>
              <span
                class="self-stretch my-auto"
                :class="{
                  'text-primary-purple': activeTab === 'watchlist',
                  'text-gray-600': activeTab !== 'watchlist',
                }"
              >
                Watchlist
              </span>
            </BaseButton>
          </div>
        </div>

        <!-- Trends Tab -->
        <div
          class="flex overflow-hidden items-center py-2 pr-3 pl-2 whitespace-nowrap rounded-md"
        >
          <div class="flex gap-3 items-center self-stretch my-auto">
            <BaseButton
              type="button"
              class="flex gap-2"
              @click="navigateToTrends"
            >
              <div
                v-html="TRENDS_ICON"
                :class="{
                  'text-primary-purple': activeTab === 'trends',
                  'text-gray-500': activeTab !== 'trends',
                }"
                class="w-5 h-5"
              ></div>
              <span
                class="self-stretch my-auto"
                :class="{
                  'text-primary-purple': activeTab === 'trends',
                  'text-gray-600': activeTab !== 'trends',
                }"
              >
                Trends
              </span>
            </BaseButton>
          </div>
        </div>
      </div>
    </div>

    <!-- User details section (same as main navbar) -->
    <div class="flex gap-4 self-start mt-1 relative">
      <div class="flex" @click="toggleDropdown" id="userProfileInsights">
        <div class="flex z-10 gap-3 items-center mr-0 font-medium">
          <div
            v-if="userStore.profilePicture"
            class="relative w-9 h-9 overflow-hidden rounded-full flex items-center justify-center"
          >
            <img
              loading="lazy"
              :src="profilePictureUrl"
              alt="User profile"
              class="w-full h-full object-cover"
            />
            <div class="absolute inset-0"></div>
          </div>
          <div v-else class="flex flex-col">
            <div
              class="flex items-center justify-center w-8 h-8 bg-primary-purple text-white rounded-full"
            >
              {{ initials() }}
            </div>
          </div>
          <div class="flex flex-col self-stretch my-auto">
            <div class="text-sm leading-none text-gray-700">
              {{ userStore.firstName }} {{ userStore.lastName }}
            </div>
            <div class="text-xs leading-none text-gray-500">
              {{ userStore.email }}
            </div>
          </div>
        </div>
        <div
          class="self-start text-xs font-black leading-none text-violet-600"
        ></div>
      </div>

      <!-- User dropdown menu -->
      <div
        v-show="showDropdown"
        class="absolute right-0 mt-8 bg-white border border-gray-200 rounded-md shadow-lg p-3 w-auto z-50"
      >
        <div class="flex flex-col gap-2 w-full">
          <BaseButton
            type="button"
            class="w-full text-left flex items-center gap-2"
            @click="goToAccount"
          >
            <div v-html="PERSON_ICON" class="text-gray-500"></div>
            <span class="text-gray-500 font-sans text-sm font-medium leading-6">
              Account
            </span>
          </BaseButton>
          <BaseButton
            type="button"
            class="w-full text-left flex items-center gap-2 logout-btn"
            @click="logout"
          >
            <div v-html="LOGOUT_ICON" class="text-red-600 ml-1"></div>
            <span class="text-gray-500 font-sans text-sm font-medium leading-6">
              Logout
            </span>
          </BaseButton>
        </div>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  INSIGHTS_SEARCH_ICON,
  TRENDS_ICON,
  INSIGHTS_LOGO_ICON,
  WATCHLIST_ICON,
} from "@/assets/svg/dashboard/svg-constants";
import { LOGOUT_ICON, PERSON_ICON } from "@/assets/svg/shared/svg-constants";
import BaseButton from "@/components/shared/BaseButton.vue";
import AppSwitcherMenu from "@/components/shared/AppSwitcherMenu.vue";
import { useUserStore } from "@/stores/user";
import { useInsightsStore } from "@/stores/insights";
import { useAttendeeStore } from "@/stores/attendee";
import { useCognitoAuth } from "@/cognito/cognito.config";
import { cognitoSignOut } from "@/cognito/auth";
import * as crossAppLogoutCookie from "@/cognito/crossAppLogoutCookie";

export default defineComponent({
  name: "InsightsNavbar",
  components: {
    BaseButton,
    AppSwitcherMenu,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const userStore = useUserStore();
    const insightsStore = useInsightsStore();
    const attendeeStore = useAttendeeStore();
    const showDropdown = ref(false);

    // Determine active tab based on route
    const activeTab = computed(() => {
      const path = route.path;
      if (path.includes("/trends")) {
        return "trends";
      }
      if (path.includes("/watchlist")) {
        return "watchlist";
      }
      return "search";
    });

    const navigateToSearch = () => {
      router.push({ name: "InsightsSearch" });
    };

    const navigateToWatchlist = () => {
      router.push({ name: "InsightsWatchlist" });
    };

    const navigateToTrends = () => {
      router.push({ name: "InsightsTrends" });
    };

    const initials = () => {
      if (userStore.firstName && userStore.lastName) {
        const firstInitial = userStore.firstName.charAt(0).toUpperCase();
        const lastInitial = userStore.lastName.charAt(0).toUpperCase();
        return `${firstInitial}${lastInitial}`;
      }
      return "";
    };

    const profilePictureUrl = computed(() => {
      return `${userStore.profilePicture}?t=${Date.now()}`;
    });

    const toggleDropdown = () => {
      showDropdown.value = !showDropdown.value;
    };

    const handleClickOutside = (event: MouseEvent) => {
      const dropdownMenu = document.querySelector("#userProfileInsights");
      const logoutButton = document.querySelector(".logout-btn");

      if (
        showDropdown.value &&
        dropdownMenu &&
        !dropdownMenu.contains(event.target as Node) &&
        (!logoutButton || !logoutButton.contains(event.target as Node))
      ) {
        showDropdown.value = false;
      }
    };

    const goToAccount = () => {
      router.push({ name: "AccountPage" });
    };

    const logout = () => {
      if (useCognitoAuth) {
        // Notify all other same-origin tabs that the user has logged out so they
        // can redirect to the login page without waiting for a guard re-check.
        try {
          crossAppLogoutCookie.setCrossAppLogoutSignalCookies(
            userStore.cognitoSub,
            userStore.email
          );
        } catch {
          /* ignore */
        }
        crossAppLogoutCookie.clearCompanyCrossAppHandoffCookiesForLogout();
        try {
          new BroadcastChannel("cognito_company_logout").postMessage({
            type: "logout",
          });
        } catch {
          /* BroadcastChannel not available in this environment — ignore */
        }
        void cognitoSignOut();
      } else {
        userStore.clearStore();
        insightsStore.clearStore();
        attendeeStore.clearStore();
        globalThis.location.href = `${process.env.VUE_APP_FRONTEGG_BASE_URL}/oauth/logout?post_logout_redirect_uri=${process.env.VUE_APP_BASE_URL}`;
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      INSIGHTS_SEARCH_ICON,
      WATCHLIST_ICON,
      TRENDS_ICON,
      INSIGHTS_LOGO_ICON,
      LOGOUT_ICON,
      PERSON_ICON,
      activeTab,
      userStore,
      showDropdown,
      profilePictureUrl,
      navigateToSearch,
      navigateToWatchlist,
      navigateToTrends,
      initials,
      toggleDropdown,
      goToAccount,
      logout,
    };
  },
});
</script>
